import { LitElement, html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { styles } from './notification-settings.style.js';
import { notificationService, type NotificationSettings } from '../../services/notification.service.js';
import type { EmailNotificationSettings } from '@cmms/models';
import { firebaseService } from '../../services/firebase.service.js';

@customElement('notification-settings')
export class NotificationSettingsComponent extends LitElement {
  static styles = styles;

  @state()
  private settings: NotificationSettings = {
    enabled: false,
    work_orders: true,
    preventive_maintenance: true,
    safety_reports: true,
    system_alerts: false
  };

  @state()
  private emailSettings: EmailNotificationSettings = {
    enabled: false,
    work_orders: true,
    preventive_maintenance: true,
    safety_reports: true,
    system_alerts: false
  };

  @state()
  private permissionStatus: NotificationPermission = 'default';

  @state()
  private isSupported = false;

  @state()
  private isLoading = false;

  @state()
  private errorMessage = '';

  @state()
  private successMessage = '';

  connectedCallback() {
    super.connectedCallback();
    console.log('[Notification Settings] Component connected');
    this.checkSupport();
    this.loadSettings();
  }

  private checkSupport() {
    this.isSupported = firebaseService.isMessagingSupported();
    if (this.isSupported) {
      this.permissionStatus = notificationService.getPermissionStatus();
    }
  }

  private async loadSettings() {
    try {
      console.log('[Notification Settings] Loading settings...');
      
      // Wait a bit for the service to be initialized
      let retries = 0;
      const maxRetries = 10;
      
      while (retries < maxRetries) {
        if (notificationService.currentUserId) {
          console.log('[Notification Settings] Service initialized, loading settings...');
          break;
        }
        
        console.log(`[Notification Settings] Service not ready, waiting... (${retries + 1}/${maxRetries})`);
        await new Promise(resolve => setTimeout(resolve, 200));
        retries++;
      }
      
      if (!notificationService.currentUserId) {
        console.error('[Notification Settings] Service not initialized after waiting');
        return;
      }
      
      const settings = await notificationService.getSettings();
      console.log('[Notification Settings] Raw settings from service:', settings);
      
      if (settings) {
        this.settings = settings;
        console.log('[Notification Settings] Applied settings to component:', this.settings);
        console.log('[Notification Settings] Settings enabled state:', this.settings.enabled);
        
        // Update permission status to match current browser state
        this.permissionStatus = notificationService.getPermissionStatus();
        console.log('[Notification Settings] Permission status:', this.permissionStatus);
        
        // Force update the component multiple ways
        console.log('[Notification Settings] Requesting component update...');
        this.requestUpdate();
        
        // Also try to force a re-render by updating the state
        setTimeout(() => {
          console.log('[Notification Settings] Forcing re-render after timeout...');
          this.requestUpdate();
        }, 100);
      } else {
        console.log('[Notification Settings] No settings returned from service');
      }
      
      // Load email settings
      await this.loadEmailSettings();
    } catch (error) {
      console.error('[Notification Settings] Error loading settings:', error);
    }
  }

  private async loadEmailSettings() {
    try {
      console.log('[Email Settings] Loading email settings...');
      
      if (!notificationService.currentUserId) {
        console.log('[Email Settings] No user ID available');
        return;
      }
      
      const userDoc = await firebaseService.firestore
        .collection('users')
        .doc(notificationService.currentUserId)
        .get();
      
      if (userDoc.exists) {
        const userData = userDoc.data();
        if (userData?.email_notification_settings) {
          this.emailSettings = userData.email_notification_settings;
          console.log('[Email Settings] Loaded email settings:', this.emailSettings);
        } else {
          console.log('[Email Settings] No email settings found, using defaults');
        }
      }
    } catch (error) {
      console.error('[Email Settings] Error loading email settings:', error);
    }
  }

  private async updateEmailSettings(updates: Partial<EmailNotificationSettings>) {
    try {
      if (!notificationService.currentUserId) {
        console.error('[Email Settings] No user ID available');
        return;
      }
      
      // Update local state
      this.emailSettings = { ...this.emailSettings, ...updates };
      
      // Update in Firestore
      await firebaseService.firestore
        .collection('users')
        .doc(notificationService.currentUserId)
        .update({
          email_notification_settings: this.emailSettings
        });
      
      console.log('[Email Settings] Updated email settings:', this.emailSettings);
      this.requestUpdate();
    } catch (error) {
      console.error('[Email Settings] Error updating email settings:', error);
      this.errorMessage = 'Failed to update email settings';
    }
  }

  private async handleEnableNotifications() {
    this.isLoading = true;
    this.errorMessage = '';
    this.successMessage = '';

    try {
      if (this.settings.enabled) {
        // Disable notifications
        await notificationService.deleteCurrentToken();
        this.settings.enabled = false;
        this.successMessage = 'Notifications disabled';
        console.log('[Notification Settings] Notifications disabled');
      } else {
        // Enable notifications - request permission
        console.log('[Notification Settings] Requesting permission...');
        const granted = await notificationService.requestPermission();
        
        if (granted) {
          this.settings.enabled = true;
          this.permissionStatus = 'granted';
          this.successMessage = 'Notifications enabled successfully!';
          console.log('[Notification Settings] Notifications enabled, settings:', this.settings);
          
          // Verify settings were saved
          setTimeout(async () => {
            const savedSettings = await notificationService.getSettings();
            console.log('[Notification Settings] Verified saved settings:', savedSettings);
          }, 500);
        } else {
          this.errorMessage = 'Permission denied. Please enable notifications in your browser settings.';
          console.log('[Notification Settings] Permission denied');
        }
      }
      
      this.requestUpdate();
      
      // Clear success message after 3 seconds
      if (this.successMessage) {
        setTimeout(() => {
          this.successMessage = '';
          this.requestUpdate();
        }, 3000);
      }
    } catch (error: any) {
      console.error('Error enabling notifications:', error);
      this.errorMessage = error.message || 'Failed to enable notifications';
    } finally {
      this.isLoading = false;
    }
  }

  private async handleSettingChange(setting: keyof NotificationSettings, value: boolean) {
    try {
      this.settings[setting] = value;
      await notificationService.updateSettings({ [setting]: value });
      this.requestUpdate();
    } catch (error) {
      console.error('Error updating setting:', error);
      this.errorMessage = 'Failed to update setting';
    }
  }

  private async handleTestNotification() {
    if (this.permissionStatus !== 'granted') {
      this.errorMessage = 'Please enable notifications first';
      return;
    }

    try {
      // Try both methods
      console.log('[Notification Settings] Testing browser notification...');
      
      // Method 1: Use notificationService
      await notificationService.sendTestNotification();
      
      // Method 2: Direct browser notification (backup test)
      setTimeout(() => {
        console.log('[Notification Settings] Sending backup test notification...');
        const backupNotification = new Notification('🔔 Backup Test', {
          body: 'This is a backup test to verify browser notifications work',
          icon: '/icon-192.png',
          tag: 'backup-test'
        });
        
        backupNotification.onclick = () => {
          backupNotification.close();
        };
        
        setTimeout(() => backupNotification.close(), 5000);
      }, 1000);
      
      this.successMessage = 'Test notification sent! Check console for diagnostics.';
      
      setTimeout(() => {
        this.successMessage = '';
        this.requestUpdate();
      }, 5000);
    } catch (error) {
      console.error('Error sending test notification:', error);
      this.errorMessage = 'Failed to send test notification';
    }
  }

  private renderNotSupported() {
    return html`
      <div class="not-supported">
        <sl-icon name="exclamation-triangle" class="warning-icon"></sl-icon>
        <h3>Notifications Not Supported</h3>
        <p>
          Your browser or device doesn't support push notifications.
          ${navigator.userAgent.includes('Safari') && navigator.userAgent.includes('iPhone') 
            ? 'iOS Safari currently does not support web push notifications.'
            : 'Please try using Chrome, Edge, or Firefox on desktop or Android.'}
        </p>
      </div>
    `;
  }

  private renderPermissionDenied() {
    return html`
      <div class="permission-denied">
        <sl-icon name="bell-slash" class="warning-icon"></sl-icon>
        <h3>Notification Permission Denied</h3>
        <p>
          You have blocked notifications for this site. To enable them:
        </p>
        <ol>
          <li>Click the lock icon in your browser's address bar</li>
          <li>Find "Notifications" in the permissions list</li>
          <li>Change it to "Allow"</li>
          <li>Refresh this page</li>
        </ol>
      </div>
    `;
  }

  render() {
    if (!this.isSupported) {
      return this.renderNotSupported();
    }

    if (this.permissionStatus === 'denied') {
      return this.renderPermissionDenied();
    }

    return html`
      <div class="notification-settings">
        <div class="header">
          <sl-icon name="bell" class="header-icon"></sl-icon>
          <div class="header-text">
            <h2>Notification Settings</h2>
            <p>Manage your notification preferences</p>
          </div>
        </div>

        ${this.errorMessage ? html`
          <sl-alert variant="danger" open closable @sl-after-hide=${() => this.errorMessage = ''}>
            <sl-icon slot="icon" name="exclamation-octagon"></sl-icon>
            ${this.errorMessage}
          </sl-alert>
        ` : ''}

        ${this.successMessage ? html`
          <sl-alert variant="success" open closable @sl-after-hide=${() => this.successMessage = ''}>
            <sl-icon slot="icon" name="check-circle"></sl-icon>
            ${this.successMessage}
          </sl-alert>
        ` : ''}

        <div class="setting-section">
          <div class="master-toggle">
            <div class="toggle-info">
              <h3>Enable Notifications</h3>
              <p>Receive push notifications for important updates</p>
            </div>
            <sl-switch
              ?checked=${this.settings.enabled}
              ?disabled=${this.isLoading}
              @sl-change=${this.handleEnableNotifications}
              size="large"
            ></sl-switch>
          </div>
        </div>

        ${this.settings.enabled ? html`
          <div class="divider"></div>

          <div class="setting-section">
            <h3>Notification Types</h3>
            <p class="section-description">Choose which notifications you want to receive</p>

            <div class="setting-item">
              <div class="setting-info">
                <sl-icon name="wrench" class="setting-icon"></sl-icon>
                <div>
                  <h4>Work Orders</h4>
                  <p>Get notified when work orders are assigned to you or updated</p>
                </div>
              </div>
              <sl-switch
                ?checked=${this.settings.work_orders}
                @sl-change=${(e: CustomEvent) => this.handleSettingChange('work_orders', (e.target as any).checked)}
              ></sl-switch>
            </div>

            <div class="setting-item">
              <div class="setting-info">
                <sl-icon name="calendar-check" class="setting-icon"></sl-icon>
                <div>
                  <h4>Preventive Maintenance</h4>
                  <p>Receive reminders for upcoming maintenance tasks</p>
                </div>
              </div>
              <sl-switch
                ?checked=${this.settings.preventive_maintenance}
                @sl-change=${(e: CustomEvent) => this.handleSettingChange('preventive_maintenance', (e.target as any).checked)}
              ></sl-switch>
            </div>

            <div class="setting-item">
              <div class="setting-info">
                <sl-icon name="exclamation-triangle" class="setting-icon"></sl-icon>
                <div>
                  <h4>Safety Reports</h4>
                  <p>Get alerts for new safety incidents and reports</p>
                </div>
              </div>
              <sl-switch
                ?checked=${this.settings.safety_reports}
                @sl-change=${(e: CustomEvent) => this.handleSettingChange('safety_reports', (e.target as any).checked)}
              ></sl-switch>
            </div>

            <div class="setting-item">
              <div class="setting-info">
                <sl-icon name="info-circle" class="setting-icon"></sl-icon>
                <div>
                  <h4>System Alerts</h4>
                  <p>General system notifications and updates</p>
                </div>
              </div>
              <sl-switch
                ?checked=${this.settings.system_alerts}
                @sl-change=${(e: CustomEvent) => this.handleSettingChange('system_alerts', (e.target as any).checked)}
              ></sl-switch>
            </div>
          </div>

          <div class="divider"></div>

          <div class="test-section">
            <sl-button
              variant="default"
              @click=${this.handleTestNotification}
            >
              <sl-icon slot="prefix" name="bell"></sl-icon>
              Send Test Notification
            </sl-button>
          </div>
        ` : ''}

        <div class="divider"></div>

        <div class="setting-section">
          <div class="master-toggle">
            <div class="toggle-info">
              <h3>Email Notifications</h3>
              <p>Receive email notifications for important updates</p>
            </div>
            <sl-switch
              ?checked=${this.emailSettings.enabled}
              @sl-change=${(e: CustomEvent) => this.updateEmailSettings({ enabled: (e.target as any).checked })}
              size="large"
            ></sl-switch>
          </div>
        </div>

        ${this.emailSettings.enabled ? html`
          <div class="divider"></div>

          <div class="setting-section">
            <h3>Email Notification Types</h3>
            <p class="section-description">Choose which email notifications you want to receive</p>

            <div class="setting-item">
              <div class="setting-info">
                <sl-icon name="wrench" class="setting-icon"></sl-icon>
                <div>
                  <h4>Work Orders</h4>
                  <p>Get email notifications when work orders are assigned to you</p>
                </div>
              </div>
              <sl-switch
                ?checked=${this.emailSettings.work_orders}
                @sl-change=${(e: CustomEvent) => this.updateEmailSettings({ work_orders: (e.target as any).checked })}
              ></sl-switch>
            </div>

            <div class="setting-item">
              <div class="setting-info">
                <sl-icon name="calendar-check" class="setting-icon"></sl-icon>
                <div>
                  <h4>Preventive Maintenance</h4>
                  <p>Receive email reminders for upcoming maintenance tasks</p>
                </div>
              </div>
              <sl-switch
                ?checked=${this.emailSettings.preventive_maintenance}
                @sl-change=${(e: CustomEvent) => this.updateEmailSettings({ preventive_maintenance: (e.target as any).checked })}
              ></sl-switch>
            </div>

            <div class="setting-item">
              <div class="setting-info">
                <sl-icon name="exclamation-triangle" class="setting-icon"></sl-icon>
                <div>
                  <h4>Safety Reports</h4>
                  <p>Get email alerts for new safety incidents and reports</p>
                </div>
              </div>
              <sl-switch
                ?checked=${this.emailSettings.safety_reports}
                @sl-change=${(e: CustomEvent) => this.updateEmailSettings({ safety_reports: (e.target as any).checked })}
              ></sl-switch>
            </div>

            <div class="setting-item">
              <div class="setting-info">
                <sl-icon name="info-circle" class="setting-icon"></sl-icon>
                <div>
                  <h4>System Alerts</h4>
                  <p>General system email notifications and updates</p>
                </div>
              </div>
              <sl-switch
                ?checked=${this.emailSettings.system_alerts}
                @sl-change=${(e: CustomEvent) => this.updateEmailSettings({ system_alerts: (e.target as any).checked })}
              ></sl-switch>
            </div>
          </div>
        ` : ''}

        <div class="info-box">
          <sl-icon name="info-circle"></sl-icon>
          <div>
            <strong>Note:</strong> Notifications work on Chrome, Edge, and Firefox (desktop and Android).
            iOS Safari does not currently support web push notifications.
          </div>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'notification-settings': NotificationSettingsComponent;
  }
}

